// Web-exclusive Home screen - Doctor Dashboard
// Matching reference design from doctor-pages.html

import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import {
  Building2, Briefcase, Globe, Clock, FileEdit, Send, Hourglass, Calendar, Star,
  BarChart3, User, HelpCircle, MessageSquare, Search, ChevronDown, Check, ChevronRight,
  ChevronLeft, ClipboardList, MapPin, DollarSign, X, Heart, CheckCircle,
} from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import UpcomingJobsModal from '../../components/web/UpcomingJobsModal.web';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getOverview } from '../../services/profile';
import { setCountry, setCurrencyPreset } from '../../reducers/auth/user';
import { getApplications, approveJob, rejectJob, getAllJobs } from '../../services/job';
import { JOB_TYPES, convertIndexToApiId } from '../../constants/jobTypes';
import { isFullTimeJob } from '../../constants/lov/professions';
import statusFilter from '../../utils/statusFilter';
import { isApplicationExpired } from '../../utils/jobApplicationStatus';
import { useIsMobile } from '../../hooks/useIsMobile';

// Redirect to facility dashboard if user is not a doctor
const HomeScreen = () => {
  const user = useSelector(state => state.user);

  // If user is a facility, redirect to facility dashboard
  if (!user?.isDoctor) {
    const FacilityHome = require('./HomeFacility.web.jsx').default;
    return <FacilityHome />;
  }

  return <DoctorDashboard />;
};

const DoctorDashboard = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const { isDoctor } = user;
  const { favorites } = useSelector(state => state.favoriteJobs);

  // State
  const [activeCategory, setActiveCategory] = useState(0); // 0 = Locum, 1 = Full-Time, etc.
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [sortBy, setSortBy] = useState('Latest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const isMobile = useIsMobile();

  // Categories with icons
  const categories = [
    { id: 0, label: 'Locum', Icon: Building2 },
    { id: 1, label: 'Full-Time', Icon: Briefcase },
    { id: 2, label: 'International', Icon: Globe },
    { id: 3, label: 'Temporary', Icon: Clock },
    { id: 4, label: 'Contract', Icon: FileEdit },
  ];

  // Status tabs
  const statusTabs = [
    { id: 'all', label: 'All' },
    { id: 'approved', label: 'Approved' },
    { id: 'pending', label: 'Pending' },
    { id: 'unfilled', label: 'Unfilled' },
    { id: 'cancelled', label: 'Cancelled' },
    { id: 'draft', label: 'Draft' },
    { id: 'history', label: 'History' },
  ];

  // Build query string for job type filter
  const jobTypeQuery = useMemo(() => {
    if (activeCategory === undefined) {
      return '';
    }
    const apiJobType = convertIndexToApiId(activeCategory);
    if (apiJobType !== undefined) {
      const draftJobType = -(apiJobType + 10);
      return `job_type=${apiJobType},${draftJobType}`;
    }
    return '';
  }, [activeCategory]);

  // Fetch dashboard data
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const overview = overviewData?.data?.data;

  useEffect(() => {
    if (overview?.country) {
      dispatch(setCurrencyPreset(overview.country));
      dispatch(setCountry(overview.country));
    }
  }, [overview]);

  const { data: applicationsData, isLoading: applicationsLoading } = useQuery({
    queryKey: ['applications', { query: jobTypeQuery }],
    queryFn: getApplications,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const allApplications = applicationsData?.data?.data ? Object.values(applicationsData.data.data) : [];

  // Fetch ALL jobs for saved jobs view (only when Saved tab is active)
  // Note: No job type filter for saved jobs - show all saved jobs regardless of category
  const { data: allJobsData, isLoading: allJobsLoading } = useQuery({
    queryKey: ['allJobs', { query: '' }],
    queryFn: getAllJobs,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const allJobs = allJobsData?.data?.data || allJobsData?.data || [];

  // Compute non-expired saved jobs count for badge
  const savedJobsCount = useMemo(() => {
    if (!allJobsData) return favorites.length;
    return allJobs.filter(job => {
      if (!favorites.includes(job.uuid)) return false;
      const isExpired = (job.end_timestamp && moment(job.end_timestamp).isBefore(moment())) || job.status === 'expired';
      return !isExpired;
    }).length;
  }, [allJobs, allJobsData, favorites]);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Accept job mutation
  const approveMutation = useMutation({
    mutationFn: approveJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['allJobs'] });
    },
    onError: (error) => {
      alert('Failed to accept job: ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  // Reject job mutation
  const rejectMutation = useMutation({
    mutationFn: rejectJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['allJobs'] });
    },
    onError: (error) => {
      alert('Failed to reject job: ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  // Handle accept with confirmation
  const handleAcceptJob = (job) => {
    if (!job.job_application?.uuid) {
      alert('Unable to accept job: Application data is missing');
      return;
    }

    const confirmed = window.confirm(
      `Accept this job at ${job.clinic?.name || job.clinic_name}?\n\n` +
      `${job.title}\n` +
      `${moment(job.start_timestamp).format('MMM D, YYYY • h:mm A')}`
    );

    if (confirmed) {
      approveMutation.mutate({ uuid: job.job_application.uuid });
    }
  };

  // Handle reject with confirmation
  const handleRejectJob = (job) => {
    if (!job.job_application?.uuid) {
      alert('Unable to reject job: Application data is missing');
      return;
    }

    const confirmed = window.confirm(
      `Reject this job offer?\n\n` +
      `${job.title} at ${job.clinic?.name || job.clinic_name}\n\n` +
      `This action cannot be undone.`
    );

    if (confirmed) {
      rejectMutation.mutate({ uuid: job.job_application.uuid });
    }
  };

  // Filter applications based on status
  const filteredApplications = useMemo(() => {
    return allApplications.filter(job => {
      const jobStatus = job.job_application_status_code;
      const isDraft = parseInt(job.type) <= -10;
      const hasApplications = job.job_applications && job.job_applications.length > 0;
      const isExpired = moment(job.end_timestamp).isBefore(moment());

      // Hide jobs that ended more than 24 hours ago (except drafts)
      if (!isDraft && job.end_timestamp) {
        const endTime = moment(job.end_timestamp);
        const twentyFourHoursAgo = moment().subtract(24, 'hours');
        if (endTime.isBefore(twentyFourHoursAgo) && (jobStatus === 4 || jobStatus === -2 || isExpired)) {
          return false;
        }
      }

      // Filter by status
      switch (activeStatusTab) {
        case 'all':
          return true;
        case 'draft':
          return isDraft;
        case 'unfilled':
          return !isDraft && !hasApplications && !isExpired;
        case 'pending':
          return !isDraft && statusFilter('pending', job);
        case 'approved':
          return !isDraft && statusFilter('approved', job, true);
        case 'cancelled':
          return !isDraft && jobStatus === -2;
        default:
          return true;
      }
    });
  }, [allApplications, activeStatusTab]);

  // Sort applications
  const sortedApplications = useMemo(() => {
    const sorted = [...filteredApplications];
    if (sortBy === 'Latest') {
      sorted.sort((a, b) => moment(a.start_timestamp) - moment(b.start_timestamp));
    } else if (sortBy === 'Oldest') {
      sorted.sort((a, b) => moment(b.start_timestamp) - moment(a.start_timestamp));
    } else if (sortBy === 'Urgent') {
      sorted.sort((a, b) => (a.is_emergency === b.is_emergency ? 0 : a.is_emergency ? -1 : 1));
    }
    return sorted;
  }, [filteredApplications, sortBy]);

  // Get all upcoming approved jobs
  const upcomingJobs = useMemo(() => {
    return allApplications
      .filter(job => statusFilter('approved', job, true))
      .filter(job => moment(job.start_timestamp).diff(moment()) > 0)
      .sort((a, b) => new Date(a.start_timestamp) - new Date(b.start_timestamp));
  }, [allApplications]);

  const upcomingJob = upcomingJobs[0];

  // Reset carousel index when upcomingJobs changes
  useEffect(() => {
    if (carouselIndex >= upcomingJobs.length) {
      setCarouselIndex(Math.max(0, upcomingJobs.length - 1));
    }
  }, [upcomingJobs.length, carouselIndex]);

  // Count jobs by category from overview.job_stats
  const categoryCounts = useMemo(() => {
    const stats = overview?.job_stats || {};
    const counts = {};
    categories.forEach(cat => {
      const key = cat.id === 0 ? 'locum' : String(convertIndexToApiId(cat.id));
      counts[cat.id] = stats[key] || 0;
    });
    return counts;
  }, [overview]);

  // Stats data
  const stats = useMemo(() => [
    { Icon: Send, label: 'Applications\nSent', value: allApplications.length, bg: '#fce7f3' },
    { Icon: Hourglass, label: 'In\nReview', value: allApplications.filter(j => statusFilter('pending', j)).length, bg: '#fef3c7' },
    { Icon: Calendar, label: 'Interview\nScheduled', value: allApplications.filter(j => statusFilter('approved', j, true)).length, bg: '#dcfce7' },
    { Icon: Star, label: 'Completed\nJobs', value: allApplications.filter(j => j.job_application_status_code === 4).length, bg: '#dbeafe' },
  ], [allApplications]);

  // Chart data
  const chartData = useMemo(() => {
    const isNonLocum = activeCategory !== 0;

    if (isNonLocum) {
      const applied = allApplications.filter(j => {
        const status = j.job_application_status_code;
        return [null, 0, 1].includes(status) && !isApplicationExpired(j);
      }).length;
      const expired = allApplications.filter(j => isApplicationExpired(j)).length;
      const maxVal = Math.max(applied, expired, 1);
      return [
        { label: 'Applied', value: applied, height: (applied / maxVal) * 100, color: '#59C2FF' },
        { label: 'Expired', value: expired, height: (expired / maxVal) * 100, color: '#808080' },
      ];
    }

    const cancelled = allApplications.filter(j => j.job_application_status_code === -2).length;
    const pending = allApplications.filter(j => statusFilter('pending', j)).length;
    const approved = allApplications.filter(j => statusFilter('approved', j, true)).length;
    const completed = allApplications.filter(j => j.job_application_status_code === 4).length;
    const maxVal = Math.max(cancelled, pending, approved, completed, 1);
    return [
      { label: 'Cancelled', value: cancelled, height: (cancelled / maxVal) * 100, color: '#fee2e2' },
      { label: 'Pending', value: pending, height: (pending / maxVal) * 100, color: '#fef3c7' },
      { label: 'Approved', value: approved, height: (approved / maxVal) * 100, color: '#22d3ee' },
      { label: 'Completed', value: completed, height: (completed / maxVal) * 100, color: '#dcfce7' },
    ];
  }, [allApplications, activeCategory]);

  // Quick actions
  const quickActions = [
    { Icon: User, label: 'My Profile', bg: '#fce7f3', action: () => navigation.navigate('ProfileDoctor') },
    { Icon: HelpCircle, label: 'Help & Support', bg: '#dcfce7', action: () => navigation.navigate('HelpSupportScreen') },
    { Icon: MessageSquare, label: 'Give us Feedback', bg: '#fef3c7', action: () => navigation.navigate('FeedbackScreen') },
  ];

  // Recent activity (mock data - can be enhanced with real data)
  const recentActivity = useMemo(() => {
    const activities = [];
    allApplications.slice(0, 4).forEach(job => {
      const status = job.job_application_status_code;
      let type = 'applied';
      let text = `Application sent to ${job.clinic?.name || job.clinic_name || 'clinic'}`;
      if (status === 3) {
        type = 'approved';
        text = `Job approved at ${job.clinic?.name || job.clinic_name || 'clinic'}`;
      }
      activities.push({
        type,
        text,
        title: job.title,
        time: formatTimeAgo(job.created_at || job.start_timestamp),
      });
    });
    return activities;
  }, [allApplications]);

  const isLoading = overviewLoading || applicationsLoading;

  return (
    <WebLayout>
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Header Row */}
        <View style={styles.headerRow}>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Dashboard</Text>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.btnBrowseJobs}
              onPress={() => navigation.navigate('Jobs')}
            >
              <Search size={16} color="#ffffff" />
              <Text style={styles.btnBrowseJobsText}>Browse Jobs</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Category Tabs */}
        <View style={[styles.categoryTabs, isMobile && styles.categoryTabsMobile]}>
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat.id}
              style={[
                styles.categoryTab,
                activeCategory === cat.id && styles.categoryTabActive,
                isMobile && styles.categoryTabMobile,
              ]}
              onPress={() => setActiveCategory(cat.id)}
            >
              <Text style={[
                styles.categoryTabText,
                activeCategory === cat.id && styles.categoryTabTextActive,
              ]}>
                {cat.label}
              </Text>
              {categoryCounts[cat.id] > 0 && (
                <View style={[
                  styles.categoryBadge,
                  activeCategory === cat.id && styles.categoryBadgeActive,
                ]}>
                  <Text style={styles.categoryBadgeText}>{categoryCounts[cat.id]}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>



        {/* Top Row Grid - Browse CTA + Upcoming */}
        {activeCategory === 0 ? (
          <View style={[styles.topRowGrid, isMobile && styles.topRowGridMobile]}>
            {/* Browse Jobs CTA */}
            <TouchableOpacity
              style={[styles.browseJobsCta, isMobile && styles.browseJobsCtaMobile]}
              onPress={() => navigation.navigate('Jobs')}
            >
              <View style={styles.ctaContent}>
                <View style={styles.ctaIcon}>
                  <Briefcase size={20} color="#ffffff" />
                </View>
                <View style={styles.ctaText}>
                  <Text style={styles.ctaTitle}>Browse & Apply</Text>
                  <Text style={styles.ctaSubtitle}>Find your next healthcare opportunity</Text>
                </View>
              </View>
              <TouchableOpacity style={styles.ctaBtn} onPress={() => navigation.navigate('Jobs')}>
                <Text style={styles.ctaBtnText}>View Jobs →</Text>
              </TouchableOpacity>
            </TouchableOpacity>

            {/* Upcoming Jobs Carousel */}
            {upcomingJobs.length > 0 && upcomingJobs[carouselIndex] ? (() => {
              const currentJob = upcomingJobs[carouselIndex];
              return (
                <View style={styles.upcomingCarousel}>
                  {/* Carousel Header */}
                  <View style={styles.carouselHeader}>
                    <Text style={styles.carouselHeaderText}>UPCOMING LOCUM JOBS</Text>
                    <View style={styles.carouselCountBadge}>
                      <Text style={styles.carouselCountBadgeText}>{upcomingJobs.length}</Text>
                    </View>
                  </View>

                  {/* Carousel Body */}
                  <TouchableOpacity
                    style={styles.carouselBody}
                    onPress={() => setShowUpcomingModal(true)}
                    activeOpacity={0.9}
                  >
                    <View style={styles.carouselContent}>
                      {/* Left Arrow */}
                      <TouchableOpacity
                        style={[styles.carouselNavBtn, carouselIndex === 0 && styles.carouselNavBtnDisabled]}
                        onPress={(e) => { e.stopPropagation(); setCarouselIndex(Math.max(0, carouselIndex - 1)); }}
                        disabled={carouselIndex === 0}
                      >
                        <ChevronLeft size={20} color={carouselIndex === 0 ? '#d1d5db' : '#6b7280'} />
                      </TouchableOpacity>

                      {/* Job Content */}
                      <View style={styles.carouselJobContent}>
                        <Text style={styles.carouselJobTitle} numberOfLines={1}>
                          {currentJob.title}
                        </Text>
                        <Text style={styles.carouselJobClinic}>
                          {currentJob.clinic?.name || currentJob.clinic_name || 'Healthcare Facility'}
                        </Text>

                        <View style={styles.carouselJobMeta}>
                          <View style={styles.carouselMetaItem}>
                            <Calendar size={14} color="#EC4899" />
                            <Text style={styles.carouselMetaText}>
                              {moment(currentJob.start_timestamp).format('D MMM')}
                            </Text>
                          </View>
                          <View style={styles.carouselMetaItem}>
                            <Clock size={14} color="#EC4899" />
                            <Text style={styles.carouselMetaText}>
                              {moment(currentJob.start_timestamp).format('h:mm A')} - {moment(currentJob.end_timestamp).format('h:mm A')}
                            </Text>
                          </View>
                        </View>

                        {currentJob.incentives && (
                          <View style={styles.carouselIncentiveBadge}>
                            <Text style={styles.carouselIncentiveBadgeText}>With other incentives</Text>
                          </View>
                        )}
                      </View>

                      {/* Right Arrow */}
                      <TouchableOpacity
                        style={[styles.carouselNavBtn, carouselIndex === upcomingJobs.length - 1 && styles.carouselNavBtnDisabled]}
                        onPress={(e) => { e.stopPropagation(); setCarouselIndex(Math.min(upcomingJobs.length - 1, carouselIndex + 1)); }}
                        disabled={carouselIndex === upcomingJobs.length - 1}
                      >
                        <ChevronRight size={20} color={carouselIndex === upcomingJobs.length - 1 ? '#d1d5db' : '#6b7280'} />
                      </TouchableOpacity>
                    </View>

                    {/* Dot Indicators */}
                    <View style={styles.carouselDots}>
                      {upcomingJobs.map((_, index) => (
                        <TouchableOpacity
                          key={index}
                          style={[styles.carouselDot, index === carouselIndex && styles.carouselDotActive]}
                          onPress={(e) => { e.stopPropagation(); setCarouselIndex(index); }}
                        />
                      ))}
                    </View>

                    {/* Check-in Button / Checked-in state */}
                    {currentJob.is_scanned ? (
                      <View style={styles.carouselCheckedInBadge}>
                        <CheckCircle size={16} color="#22c55e" />
                        <Text style={styles.carouselCheckedInText}>Checked In</Text>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={styles.carouselCheckInBtn}
                        onPress={(e) => { e.stopPropagation(); setShowUpcomingModal(true); }}
                      >
                        <Text style={styles.carouselCheckInBtnText}>Check In</Text>
                      </TouchableOpacity>
                    )}
                  </TouchableOpacity>
                </View>
              );
            })() : (
              <View style={styles.noUpcomingCard}>
                <Text style={styles.noUpcomingText}>No upcoming jobs scheduled</Text>
                <TouchableOpacity
                  style={styles.noUpcomingBtn}
                  onPress={() => navigation.navigate('Jobs')}
                >
                  <Text style={styles.noUpcomingBtnText}>Find Jobs</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ) : (
          // Full-width CTA for non-Locum categories
          <TouchableOpacity
            style={[styles.browseJobsCtaExpanded, isMobile && styles.browseJobsCtaExpandedMobile]}
            onPress={() => navigation.navigate('Jobs')}
          >
            <View style={styles.ctaContentExpanded}>
              <View style={styles.ctaIconExpanded}>
                <Briefcase size={24} color="#ffffff" />
              </View>
              <View style={styles.ctaTextExpanded}>
                <Text style={styles.ctaTitleExpanded}>Browse & Apply</Text>
                <Text style={styles.ctaSubtitleExpanded}>
                  Find your next healthcare opportunity. Browse thousands of positions and connect with top employers.
                </Text>
              </View>
            </View>
            <TouchableOpacity style={styles.ctaBtnExpanded} onPress={() => navigation.navigate('Jobs')}>
              <Text style={styles.ctaBtnTextExpanded}>View Jobs →</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        )}
              {/* Application Status Chart */}
              <View style={styles.chartCard}>
          <View style={styles.chartHeader}>
            <Text style={styles.chartTitle}>Application Status</Text>
          </View>
          <View style={styles.chartContainer}>
            {chartData.map((bar, index) => (
              <View key={index} style={styles.chartBarWrapper}>
                <View style={[styles.chartBar, { height: Math.max(bar.height, 10), backgroundColor: bar.color }]} />
                <Text style={styles.chartBarLabel}>{bar.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* My Jobs Section */}
        <View style={styles.myJobsCard}>
          <View style={styles.myJobsHeader}>
            <Text style={styles.myJobsTitle}>My Jobs</Text>
            <TouchableOpacity
              style={styles.sortDropdownWrapper}
              onPress={() => setShowSortDropdown(!showSortDropdown)}
            >
              <View style={styles.sortPill}>
                <Text style={styles.sortPillText}>Sort by: </Text>
                <Text style={styles.sortPillValue}>{sortBy}</Text>
                <View style={[styles.sortArrow, showSortDropdown && styles.sortArrowOpen]}>
                  <ChevronDown size={12} color={webColors.textMuted} />
                </View>
              </View>
              {showSortDropdown && (
                <View style={styles.sortDropdownMenu}>
                  {['Latest', 'Oldest', 'Urgent'].map(option => (
                    <TouchableOpacity
                      key={option}
                      style={[styles.sortDropdownItem, sortBy === option && styles.sortDropdownItemActive]}
                      onPress={() => { setSortBy(option); setShowSortDropdown(false); }}
                    >
                      <Text style={[styles.sortDropdownItemText, sortBy === option && styles.sortDropdownItemTextActive]}>
                        {option}
                      </Text>
                      {sortBy === option && <Check size={14} color={webColors.primary} />}
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </TouchableOpacity>
          </View>

          {/* Status Tabs */}
          <View style={styles.statusTabsRow}>
            {statusTabs.map(tab => (
              <TouchableOpacity
                key={tab.id}
                style={[styles.statusTab, activeStatusTab === tab.id && styles.statusTabActive]}
                onPress={() => tab.id === 'history' ? navigation.navigate('JobHistory') : setActiveStatusTab(tab.id)}
              >
                <Text style={[styles.statusTabText, activeStatusTab === tab.id && styles.statusTabTextActive]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Job Cards List */}
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={webColors.primary} />
            </View>
          ) : sortedApplications.length > 0 ? (
            <View style={styles.myJobsList}>
              {sortedApplications.slice(0, 5).map((job, index) => (
                <JobCard
                  key={job.uuid || index}
                  job={job}
                  onPress={() => navigation.navigate('DetailsScreen', { data: job, jobId: job.uuid })}
                  onAccept={() => handleAcceptJob(job)}
                  onReject={() => handleRejectJob(job)}
                  isAccepting={approveMutation.isPending && approveMutation.variables?.uuid === job.job_application?.uuid}
                  isRejecting={rejectMutation.isPending && rejectMutation.variables?.uuid === job.job_application?.uuid}
                />
              ))}
            </View>
          ) : (
            <View style={styles.myJobsEmptyState}>
              <View style={styles.emptyStateIcon}>
                <ClipboardList size={48} color="#9ca3af" />
              </View>
              <Text style={styles.emptyStateText}>No {activeStatusTab} jobs</Text>
              <TouchableOpacity onPress={() => navigation.navigate('Jobs')}>
                <Text style={styles.emptyStateLink}>Apply for a new job</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>


        {/* Quick Actions + Recent Activity Grid */}
        <View style={[styles.actionsActivityGrid, isMobile && styles.actionsActivityGridMobile]}>
          {/* Quick Actions */}
          <View style={styles.quickActionsCard}>
            <Text style={styles.quickActionsHeader}>Quick Actions</Text>
            {quickActions.map((action, index) => (
              <TouchableOpacity key={index} style={styles.quickActionRow} onPress={action.action}>
                <View style={[styles.quickActionIconBox, { backgroundColor: action.bg }]}>
                  <action.Icon size={14} color={webColors.textPrimary} />
                </View>
                <Text style={styles.quickActionLabel}>{action.label}</Text>
                <ChevronRight size={16} color="#d1d5db" />
              </TouchableOpacity>
            ))}
          </View>

          {/* Recent Activity */}
          <View style={styles.recentActivityCard}>
            <View style={styles.recentActivityHeader}>
              <Text style={styles.recentActivityTitle}>Recent Activity</Text>
                <TouchableOpacity onPress={() => navigation.navigate('Inbox')}>
                <Text style={styles.recentActivityLink}>View All →</Text>
              </TouchableOpacity>
            </View>
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <View key={index} style={styles.activityRow}>
                  <View style={[styles.activityIndicator, activity.type === 'approved' ? styles.activityApproved : styles.activityApplied]}>
                    {activity.type === 'approved' && <Check size={10} color="#22c55e" />}
                  </View>
                  <View style={styles.activityInfo}>
                    <Text style={styles.activityText}>
                      <Text style={styles.activityTextBold}>{activity.title}</Text>
                    </Text>
                    <Text style={styles.activityTextSub}>{activity.text}</Text>
                    <Text style={styles.activityTime}>{activity.time}</Text>
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.emptyActivity}>
                <Text style={styles.emptyActivityText}>No recent activity</Text>
              </View>
            )}
          </View>
        </View>

  
        <View style={{ height: 60 }} />
      </ScrollView>

      {/* Upcoming Jobs Modal */}
      <UpcomingJobsModal
        visible={showUpcomingModal}
        jobs={upcomingJobs}
        onClose={() => {
          setShowUpcomingModal(false);
          queryClient.invalidateQueries({ queryKey: ['applications'] });
          queryClient.invalidateQueries({ queryKey: ['overview'] });
        }}
      />
    </WebLayout>
  );
};

// Job Card Component
const JobCard = ({ job, onPress, onAccept, onReject, isAccepting, isRejecting }) => {
  const isMobile = useIsMobile();
  const statusCode = job.job_application_status_code;
  const statusLabel = getStatusLabel(statusCode, job.type);
  const statusStyle = getStatusBadgeStyle(statusCode, job.type);
  const clinicName = job.clinic?.name || job.clinic_name || 'Healthcare Facility';
  const location = job.clinic?.city || job.city || job.location || '';

  // Check if job is awaiting doctor acceptance
  const isPendingDoctor = statusCode === 1;

  return (
    <TouchableOpacity style={[styles.historyJobCard, isMobile && styles.historyJobCardMobile]} onPress={onPress}>
      <View style={[styles.jobLogo, { backgroundColor: getLogoColor(clinicName) }]}>
        <Text style={styles.jobLogoText}>{getInitials(clinicName)}</Text>
      </View>
      <View style={styles.jobInfo}>
        {job.subtitle ? (
          <View style={styles.jobSubtitlePill}>
            <Text style={styles.jobSubtitleText}>{job.subtitle}</Text>
          </View>
        ) : null}
        <Text style={styles.jobTitle} numberOfLines={1}>{job.title}</Text>
        <Text style={styles.jobCompany}>{clinicName}</Text>
        <View style={styles.jobMeta}>
          {location && (
            <View style={styles.jobMetaItemRow}>
              <MapPin size={14} color={webColors.textMuted} />
              <Text style={styles.jobMetaItem}>{location}</Text>
            </View>
          )}
          {job.price && (
            <View style={styles.jobMetaItemRow}>
              <DollarSign size={14} color={webColors.textMuted} />
              <Text style={styles.jobMetaItem}>RM {job.price}/hr</Text>
            </View>
          )}
          {job.duration_hours && (
            <View style={styles.jobMetaItemRow}>
              <Clock size={14} color={webColors.textMuted} />
              <Text style={styles.jobMetaItem}>{job.duration_hours} hours</Text>
            </View>
          )}
        </View>
        <View style={[styles.historyJobBadge, statusStyle]}>
          <Text style={[styles.historyJobBadgeText, { color: statusStyle.textColor }]}>{statusLabel}</Text>
        </View>
        <Text style={styles.jobDateInfo}>
          {job.start_timestamp ? moment(job.start_timestamp).format('MMM D, YYYY • h:mm A') : ''}
        </Text>
      </View>

      {/* Action Buttons - Show Accept/Reject for PENDING_DOCTOR status */}
      {isPendingDoctor && onAccept && onReject ? (
        <View style={styles.jobActions}>
          <TouchableOpacity
            style={[styles.btnAccept, (isAccepting || isRejecting) && styles.btnDisabled]}
            onPress={(e) => { e.stopPropagation(); onAccept(); }}
            disabled={isAccepting || isRejecting}
          >
            {isAccepting ? <Hourglass size={14} color="#ffffff" /> : <Check size={14} color="#ffffff" />}
            <Text style={styles.btnAcceptText}>{isAccepting ? 'Accepting...' : 'Accept'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.btnReject, (isAccepting || isRejecting) && styles.btnDisabled]}
            onPress={(e) => { e.stopPropagation(); onReject(); }}
            disabled={isAccepting || isRejecting}
          >
            {isRejecting ? <Hourglass size={14} color="#ffffff" /> : <X size={14} color="#ffffff" />}
            <Text style={styles.btnRejectText}>{isRejecting ? 'Rejecting...' : 'Reject'}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity style={styles.btnViewJob} onPress={onPress}>
          <Text style={styles.btnViewJobText}>View</Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

// Helper functions
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const getLogoColor = (name) => {
  const colors = ['#fce7f3', '#dbeafe', '#dcfce7', '#fef3c7', '#f3e8ff', '#e0f2fe'];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

const getLogoTextColor = (name) => {
  const colors = ['#EC4899', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#0ea5e9'];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

const formatTimeAgo = (date) => {
  if (!date) return '';
  const m = moment(date);
  const now = moment();
  const diff = now.diff(m, 'hours');
  if (diff < 1) return 'Just now';
  if (diff < 24) return `${diff} hours ago`;
  const days = now.diff(m, 'days');
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return m.format('MMM D');
};

const getStatusLabel = (statusCode, jobType) => {
  if (statusCode === 0 && isFullTimeJob(jobType)) return 'Applied';
  const labels = {
    0: 'Pending',
    1: 'Pending',
    2: 'Pending',
    3: 'Approved',
    4: 'Completed',
    '-2': 'Cancelled',
    '-3': 'Expired',
  };
  return labels[statusCode] || 'Pending';
};

const getStatusBadgeStyle = (statusCode, jobType) => {
  if (statusCode === 0 && isFullTimeJob(jobType)) {
    return { backgroundColor: '#d1f4e0', textColor: '#0d9f6e' };
  }
  const styles = {
    0: { backgroundColor: '#fef3c7', textColor: '#92400e' },
    1: { backgroundColor: '#fef3c7', textColor: '#92400e' },
    2: { backgroundColor: '#fef3c7', textColor: '#92400e' },
    3: { backgroundColor: '#dbeafe', textColor: '#1d4ed8' },
    4: { backgroundColor: '#d1f4e0', textColor: '#0d9f6e' },
    '-2': { backgroundColor: '#fee2e2', textColor: '#991b1b' },
    '-3': { backgroundColor: '#fef3c7', textColor: '#92400e' },
  };
  return styles[statusCode] || styles[0];
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },
  btnBrowseJobs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  btnBrowseJobsIcon: {
    fontSize: 16,
  },
  btnBrowseJobsText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Category Tabs
  categoryTabs: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  categoryTab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: webColors.border,
    backgroundColor: webColors.cardBackground,
    alignItems: 'center',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  categoryTabActive: {
    backgroundColor: webColors.primary,
    borderColor: webColors.primary,
  },
  categoryTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textMuted,
  },
  categoryTabTextActive: {
    color: '#ffffff',
  },
  categoryBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 20,
    height: 20,
    backgroundColor: webColors.primary,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
    borderWidth: 2,
    borderColor: '#f8f9fa',
  },
  categoryBadgeActive: {
    backgroundColor: '#be185d',
  },
  categoryBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '600',
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row',
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
    marginBottom: 24,
  },
  statCard: {
    flex: 1,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  statCardBorder: {
    borderRightWidth: 1,
    borderRightColor: '#f3f4f6',
  },
  statIconBox: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statIcon: {
    fontSize: 17,
  },
  statValue: {
    fontSize: 26,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  statLabel: {
    fontSize: 12,
    color: webColors.textMuted,
    lineHeight: 16,
  },
  // Top Row Grid
  topRowGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 24,
  },
  // Browse CTA
  browseJobsCta: {
    flex: 1,
    background: 'linear-gradient(135deg, #667eea 0%, #EC4899 100%)',
    backgroundColor: '#EC4899',
    borderRadius: 16,
    padding: 24,
    paddingHorizontal: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  },
  ctaContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  ctaIcon: {
    width: 44,
    height: 44,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaIconText: {
    fontSize: 20,
  },
  ctaText: {},
  ctaTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 2,
  },
  ctaSubtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.85)',
  },
  ctaBtn: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  ctaBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  // Expanded CTA
  browseJobsCtaExpanded: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #EC4899 100%)',
    backgroundColor: '#667eea',
    borderRadius: 16,
    padding: 24,
    paddingHorizontal: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 24,
  },
  ctaContentExpanded: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    flex: 1,
  },
  ctaIconExpanded: {
    width: 56,
    height: 56,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaIconTextExpanded: {
    fontSize: 24,
  },
  ctaTextExpanded: {},
  ctaTitleExpanded: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 4,
  },
  ctaSubtitleExpanded: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 20,
    maxWidth: 500,
  },
  ctaBtnExpanded: {
    paddingHorizontal: 28,
    paddingVertical: 14,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  ctaBtnTextExpanded: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  // Upcoming Card
  upcomingCard: {
    flex: 1,
    background: 'linear-gradient(135deg, #fdf2f8 0%, #faf5ff 100%)',
    backgroundColor: '#fdf2f8',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#fce7f3',
    padding: 16,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  datePill: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    minWidth: 70,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
  },
  dateMonth: {
    fontSize: 10,
    fontWeight: '600',
    color: webColors.primary,
    textTransform: 'uppercase',
  },
  dateDay: {
    fontSize: 26,
    fontWeight: '700',
    color: webColors.textPrimary,
    lineHeight: 30,
  },
  upcomingInfo: {
    flex: 1,
  },
  upcomingTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  upcomingLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: webColors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  upcomingCountBadge: {
    backgroundColor: webColors.primary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  upcomingCountBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '700',
  },
  upcomingTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    lineHeight: 20,
  },
  upcomingCompany: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  btnViewDetails: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  btnViewDetailsText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  // Upcoming Jobs Carousel
  upcomingCarousel: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#fce7f3',
    overflow: 'hidden',
  },
  carouselHeader: {
    background: 'linear-gradient(135deg, #EC4899 0%, #f472b6 100%)',
    backgroundColor: '#EC4899',
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  carouselHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.5,
  },
  carouselCountBadge: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  carouselCountBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#EC4899',
  },
  carouselBody: {
    padding: 16,
  },
  carouselContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  carouselNavBtn: {
    padding: 6,
    borderRadius: 50,
    backgroundColor: 'transparent',
  },
  carouselNavBtnDisabled: {
    opacity: 0.4,
  },
  carouselJobContent: {
    flex: 1,
    minWidth: 0,
  },
  carouselJobTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  carouselJobClinic: {
    fontSize: 13,
    color: webColors.textSecondary,
    marginBottom: 10,
  },
  carouselJobMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  carouselMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  carouselMetaText: {
    fontSize: 13,
    color: webColors.textSecondary,
    fontWeight: '500',
  },
  carouselIncentiveBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#dcfce7',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 10,
  },
  carouselIncentiveBadgeText: {
    fontSize: 11,
    color: '#22c55e',
    fontWeight: '600',
  },
  carouselDots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 16,
  },
  carouselDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e5e7eb',
  },
  carouselDotActive: {
    width: 20,
    backgroundColor: '#EC4899',
  },
  carouselCheckInBtn: {
    backgroundColor: '#EC4899',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  carouselCheckInBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  carouselCheckedInBadge: {
    backgroundColor: '#dcfce7',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  carouselCheckedInText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#22c55e',
  },
  // No upcoming
  noUpcomingCard: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noUpcomingIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  noUpcomingText: {
    color: webColors.textMuted,
    fontSize: 14,
    marginBottom: 12,
  },
  noUpcomingBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.small,
  },
  noUpcomingBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Actions Activity Grid
  actionsActivityGrid: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 24,
  },
  // Quick Actions Card
  quickActionsCard: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
  },
  quickActionsHeader: {
    padding: 14,
    paddingHorizontal: 16,
    fontSize: 13,
    fontWeight: '700',
    color: webColors.textPrimary,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  quickActionRow: {
    padding: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  quickActionIconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickActionIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickActionLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  quickActionChevron: {
    color: '#d1d5db',
    fontSize: 16,
  },
  // Recent Activity Card
  recentActivityCard: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
  },
  recentActivityHeader: {
    padding: 14,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  recentActivityTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  recentActivityLink: {
    fontSize: 12,
    fontWeight: '500',
    color: webColors.primary,
  },
  activityRow: {
    padding: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  activityIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  activityApplied: {
    backgroundColor: '#fce7f3',
  },
  activityApproved: {
    backgroundColor: '#dcfce7',
  },
  activityCheck: {
    color: '#22c55e',
    fontSize: 10,
    fontWeight: '700',
  },
  activityInfo: {
    flex: 1,
  },
  activityText: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 18,
  },
  activityTextBold: {
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  activityTextSub: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  activityTime: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 4,
  },
  emptyActivity: {
    padding: 24,
    alignItems: 'center',
  },
  emptyActivityText: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  // Chart Card
  chartCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 24,
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  chartContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: 140,
    paddingHorizontal: 10,
  },
  chartBarWrapper: {
    alignItems: 'center',
    gap: 8,
  },
  chartBar: {
    width: 70,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  chartBarLabel: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  // My Jobs Card
  myJobsCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
  },
  myJobsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  myJobsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  sortDropdownWrapper: {
    position: 'relative',
    zIndex: 100,
  },
  sortPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 20,
  },
  sortPillText: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  sortPillValue: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  sortArrow: {
    marginLeft: 4,
  },
  sortArrowOpen: {
    transform: [{ rotate: '180deg' }],
  },
  sortDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    minWidth: 150,
    overflow: 'hidden',
    zIndex: 100,
  },
  sortDropdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  sortDropdownItemActive: {
    backgroundColor: '#f9fafb',
  },
  sortDropdownItemText: {
    fontSize: 14,
    color: '#374151',
  },
  sortDropdownItemTextActive: {
    color: webColors.primary,
    fontWeight: '600',
  },
  sortDropdownCheck: {
    color: webColors.primary,
    fontSize: 14,
  },
  // Status Tabs
  statusTabsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  statusTab: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: webColors.border,
    backgroundColor: webColors.cardBackground,
  },
  statusTabActive: {
    backgroundColor: '#1a1a1a',
    borderColor: '#1a1a1a',
  },
  statusTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textMuted,
  },
  statusTabTextActive: {
    color: '#ffffff',
  },
  // Saved Tab Styles
  savedTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  savedTabActive: {
    backgroundColor: webColors.primary,
    borderColor: webColors.primary,
  },
  savedBadge: {
    minWidth: 18,
    height: 18,
    backgroundColor: webColors.primaryLight,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  savedBadgeActive: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  savedBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: webColors.primary,
  },
  savedBadgeTextActive: {
    color: '#ffffff',
  },
  savedJobBadge: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: webColors.primaryLight,
    marginBottom: 8,
  },
  savedJobBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.primary,
  },
  historyBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  historyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  // Job Cards
  myJobsList: {
    gap: 16,
  },
  historyJobCard: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: '#f3f4f6',
    borderRadius: 20,
    padding: 28,
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  jobLogo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  jobLogoText: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.primary,
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 6,
  },
  jobSubtitlePill: {
    alignSelf: 'flex-start',
    background: 'linear-gradient(135deg, #818cf8 0%, #7c3aed 100%)',
    backgroundColor: '#7c3aed',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 8,
  },
  jobSubtitleText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  jobCompany: {
    fontSize: 15,
    color: '#5B9FF9',
    marginBottom: 12,
    fontWeight: '500',
  },
  jobMeta: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  jobMetaItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  jobMetaItem: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  historyJobBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 10,
    marginBottom: 8,
  },
  historyJobBadgeText: {
    fontSize: 13,
    fontWeight: '600',
  },
  jobDateInfo: {
    fontSize: 13,
    color: '#9ca3af',
  },
  btnViewJob: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.primary,
    borderRadius: 10,
    alignSelf: 'center',
  },
  btnViewJobText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
  },
  // Job Actions Container (Accept/Reject buttons)
  jobActions: {
    flexDirection: 'column',
    gap: 10,
    alignSelf: 'center',
    minWidth: 120,
  },
  // Accept Button
  btnAccept: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#22c55e',
    borderRadius: webBorderRadius.medium,
    minWidth: 120,
  },
  btnAcceptIcon: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '700',
  },
  btnAcceptText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Reject Button
  btnReject: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#ef4444',
    borderRadius: webBorderRadius.medium,
    minWidth: 120,
  },
  btnRejectIcon: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '700',
  },
  btnRejectText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Disabled Button State
  btnDisabled: {
    opacity: 0.5,
  },
  // Empty State
  myJobsEmptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateIcon: {
    marginBottom: 16,
    opacity: 0.5,
  },
  emptyStateText: {
    fontSize: 15,
    color: '#9ca3af',
    marginBottom: 12,
  },
  emptyStateLink: {
    fontSize: 15,
    color: webColors.primary,
    fontWeight: '600',
  },
  // ==================== MOBILE VARIANTS ====================
  pageTitleMobile: {
    fontSize: 22,
  },
  categoryTabsMobile: {
    flexWrap: 'wrap',
  },
  categoryTabMobile: {
    flexBasis: '47%',
    flexGrow: 1,
  },
  topRowGridMobile: {
    flexDirection: 'column',
  },
  browseJobsCtaMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
  browseJobsCtaExpandedMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    paddingHorizontal: 20,
  },
  actionsActivityGridMobile: {
    flexDirection: 'column',
  },
  historyJobCardMobile: {
    flexDirection: 'column',
    padding: 18,
    gap: 16,
  },
});

export default HomeScreen;
